import { motion } from 'framer-motion';
import { Users, Truck, Factory } from 'lucide-react';

const products = [
  {
    id: 'recruitment',
    icon: Users,
    title: 'Intake Agent for Recruitment',
    description: 'AI-assisted intake and triage for recruitment workflows. Structures candidate and client requests, prioritizes cases, reduces admin load, and improves processing consistency across teams.',
    image: 'https://images.unsplash.com/photo-1758518731468-98e90ffd7430?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwY29ycG9yYXRlJTIwdGVhbSUyMG1lZXRpbmclMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzcyOTA3MTQ3fDA&ixlib=rb-4.1.0&q=85',
  },
  {
    id: 'logistics',
    icon: Truck,
    title: 'Intake Agent for Logistics',
    description: 'Automation for incoming logistics requests and coordination flows. Organizes information, validates required fields, reduces communication friction, and supports operational handling.',
    image: 'https://images.unsplash.com/photo-1761195696590-3490ea770aa1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBsb2dpc3RpY3MlMjBhdXRvbWF0aW9ufGVufDB8fHx8MTc3MjkwNzE0NXww&ixlib=rb-4.1.0&q=85',
  },
  {
    id: 'manufacturing',
    icon: Factory,
    title: 'Intake Agent for Operations',
    description: 'AI-supported intake and workflow structuring for operations and manufacturing. Improves visibility into requests, reduces manual bottlenecks, and supports process coordination.',
    image: 'https://images.unsplash.com/photo-1752614671052-92e18f534db1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxhdXRvbWF0ZWQlMjBmYWN0b3J5JTIwbWFudWZhY3R1cmluZyUyMHJvYm90JTIwYXJtfGVufDB8fHx8MTc3MjkwNzE0Nnww&ixlib=rb-4.1.0&q=85',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

export const IndustryProducts = () => {
  return (
    <section
      id="industries"
      data-testid="industry-products-section"
      className="bg-slate-50 py-24 md:py-32 border-t border-slate-100"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="mb-16 max-w-3xl"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-4">
            Industry Focus
          </p>
          <h2
            data-testid="industries-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 font-['Manrope'] mb-6"
          >
            Industry-specific intake agents
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            Purpose-built automation for industries with high-volume inbound workflows and operational complexity.
          </p>
        </motion.div>

        {/* Product Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        >
          {products.map((product) => (
            <motion.div
              key={product.id}
              variants={itemVariants}
              data-testid={`industry-card-${product.id}`}
              className="group bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-slate-300 transition-colors"
            >
              {/* Image */}
              <div className="relative h-44 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-slate-900/50 group-hover:bg-slate-900/40 transition-colors" />
                <div className="absolute top-5 left-5">
                  <div className="p-2.5 bg-white/10 backdrop-blur-sm rounded-sm">
                    <product.icon className="text-white" size={20} strokeWidth={1.5} />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-7">
                <h3 className="text-lg font-semibold text-slate-900 mb-3 font-['Manrope']">
                  {product.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {product.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
